import { useState } from "react";
import { useSiteConfig } from "@/contexts/SiteConfigContext";
import api, { formatApiError } from "@/lib/api";
import { FilterChips } from "@/components/site/parts";
import { useCopy } from "@/lib/copy";

const EMPTY = { name: "", email: "", message: "" };

export default function Contact() {
  const { config } = useSiteConfig();
  const t = useCopy();
  const kinds = t("contact.kinds").split(",").map((k) => k.trim()).filter(Boolean);
  const [kind, setKind] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [sentAs, setSentAs] = useState(null);
  const [error, setError] = useState("");

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/contact", { ...form, subject: kind || kinds[0] });
      setSentAs(form.name.trim().split(/\s+/)[0] || "there");
      setForm(EMPTY);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const links = [
    config.email && { href: `mailto:${config.email}`, text: config.email, tag: "EMAIL" },
    ...(config.social_links || []).map((s) => ({ href: s.url, text: `${s.url.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")} ↗`, tag: s.label.toUpperCase(), ext: true })),
  ].filter(Boolean);

  return (
    <section className="bx" style={{ paddingTop: 16 }}>
      <div className="contact-panel padl split" style={{ alignItems: "start", padding: 48, borderRadius: 28 }}>
        <div className="rise d1" style={{ display: "flex", flexDirection: "column", gap: 22, minWidth: 0 }}>
          <span className="mono" style={{ fontSize: 12, color: "#DCE4FF", textTransform: "uppercase" }}>{t("contact.kicker")}</span>
          <h1 className="hero-h stretch" style={{ fontSize: "clamp(48px, 4.8vw, 68px)", lineHeight: 0.9, animationDelay: ".2s" }}>
            {t("contact.title")} <span className="em-y">{t("contact.accent")}</span>
          </h1>
          <p style={{ margin: 0, fontSize: 18, lineHeight: 1.6, color: "#E4EAFF" }}>
            {t("contact.sub")} {t("contact.reply_time")}
          </p>
          <div className="contact-links">
            {links.map((l) => (
              <a key={l.href} href={l.href} {...(l.ext ? { target: "_blank", rel: "noopener noreferrer" } : {})} data-testid={l.tag === "EMAIL" ? "contact-email" : undefined}>
                <span style={{ overflowWrap: "anywhere" }}>{l.text}</span>
                <span className="mono">{l.tag}</span>
              </a>
            ))}
          </div>
        </div>
        <div className="rise d3 contact-form-card">
          {sentAs ? (
            <div role="status" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 16, padding: "24px 0" }}>
              <svg className="flag" aria-hidden="true" width="90" height="60" viewBox="0 0 90 60">
                <line x1="4" y1="0" x2="4" y2="60" stroke="#15171C" strokeWidth="4" />
                {Array.from({ length: 18 }).map((_, k) => {
                  const r = Math.floor(k / 6);
                  const c = k % 6;
                  return <rect key={k} x={6 + c * 14} y={r * 14} width="14" height="14" fill={(r + c) % 2 ? "#FFFFFF" : "#15171C"} />;
                })}
              </svg>
              <span className="disp" style={{ fontSize: 36, fontWeight: 900, textTransform: "uppercase", lineHeight: 1 }}>{t("contact.sent_title")}</span>
              <span style={{ fontSize: 16, lineHeight: 1.6, color: "#4A4F5A" }}>{t("contact.sent_body", { name: sentAs })}</span>
              <button type="button" className="btn btn-line" style={{ background: "#fff" }} onClick={() => setSentAs(null)}>Send another</button>
            </div>
          ) : (
            <form onSubmit={onSubmit} data-testid="contact-form" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <span className="disp" style={{ fontSize: 22, fontWeight: 900, textTransform: "uppercase" }}>{t("contact.form_title")}</span>
              {kinds.length > 0 && <FilterChips label="What is it about" options={kinds} value={kind || kinds[0]} onChange={setKind} />}
              <label className="lbl">Name<input className="fld" type="text" name="name" autoComplete="name" required value={form.name} onChange={onChange} data-testid="contact-field-name" /></label>
              <label className="lbl">Email<input className="fld" type="email" name="email" autoComplete="email" required value={form.email} onChange={onChange} data-testid="contact-field-email" /></label>
              <label className="lbl">Message<textarea className="fld" name="message" rows={5} required value={form.message} onChange={onChange} data-testid="contact-field-message" /></label>
              {error && <p role="alert" style={{ margin: 0, color: "#C42B2B", fontWeight: 600 }}>{error}</p>}
              <button type="submit" className="btn btn-ink" disabled={submitting} style={{ height: 56, borderRadius: 14, fontSize: 17, boxShadow: "0 5px 0 #0B0C0F" }} data-testid="contact-submit">
                {submitting ? "Sending…" : t("contact.submit")}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
